import type {
  CaseSubType,
  CaseType,
} from '@repo/internal-tools-api/src/services/cases/types.ts'
import type { RadioOption } from '@repo/internal-tools-design-system/src/lib/RadioGroupField/RadioGroupField.tsx'

const CARD_DISPUTE_CASE_SUB_TYPE_FILTER_VALUES = [
  'Fraud',
  'NonFraud',
] as const satisfies CaseSubType[]

const VISA_RISK_MANAGER_CASE_SUB_TYPE_FILTER_VALUES = [
  'VrmAlert',
  'RaaSAlert',
] as const satisfies CaseSubType[]

export const CASE_SUB_TYPE_FILTER_VALUES = [
  ...CARD_DISPUTE_CASE_SUB_TYPE_FILTER_VALUES,
  ...VISA_RISK_MANAGER_CASE_SUB_TYPE_FILTER_VALUES,
] as const

const CASE_SUB_TYPES_BY_CASE_TYPE: Partial<
  Record<CaseType, readonly CaseSubTypeFilterValue[]>
> = {
  CardDispute: CARD_DISPUTE_CASE_SUB_TYPE_FILTER_VALUES,
  VisaRiskManager: VISA_RISK_MANAGER_CASE_SUB_TYPE_FILTER_VALUES,
}

export type CaseSubTypeFilterValue =
  (typeof CASE_SUB_TYPE_FILTER_VALUES)[number]

export type CaseSubTypeFilter = CaseSubTypeFilterValue | undefined

/**
 * Get unique case sub-types based on case types
 * @param caseTypes - Array of case types for the user
 * @returns Array of unique case sub-types for the case types
 */
const getCaseSubTypesForCaseTypes = (
  caseTypes: CaseType[],
): CaseSubTypeFilterValue[] => {
  const subTypesSet = new Set<CaseSubTypeFilterValue>()

  caseTypes.forEach((caseType) => {
    const subTypes = CASE_SUB_TYPES_BY_CASE_TYPE[caseType]
    subTypes?.forEach((subType) => subTypesSet.add(subType))
  })

  return Array.from(subTypesSet)
}

/**
 * Generate filter options for case sub-types applicable to the given case types
 */
export const getCaseSubTypeFilterOptions = (
  caseTypes: CaseType[],
): RadioOption<CaseSubTypeFilterValue>[] =>
  getCaseSubTypesForCaseTypes(caseTypes).map(
    (subType) =>
      ({
        label: subType,
        value: subType,
        'data-testid': `radio-group-item-${subType}`,
      }) satisfies RadioOption<CaseSubTypeFilterValue>,
  )
